<template>
  <div class="user-dashboard">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
      </ul>
    </nav>
    
    <div class="level">
      <div class="level-left">
        <h1 class="title">Dashboard</h1>
      </div>
      <div class="level-right">
        <div class="field is-grouped">
          <div class="control">
            <div class="select is-small">
              <select v-model="quickFilter" @change="applyQuickFilter">
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
              </select>
            </div>
          </div>
          <div class="control">
            <button class="button is-light is-small" @click="toggleFilters">
              <span class="icon is-small">
                <i class="fas fa-filter"></i>
              </span>
              <span>Show Filters</span>
            </button>
          </div>
          <div class="control">
            <button 
              class="button is-info is-small notification-button" 
              @click="toggleNotificationPanel"
              :class="{ 'has-notifications': hasNotifications }"
            >
              <span class="icon is-small">
                <font-awesome-icon icon="bell" />
              </span>
              <span v-if="totalNotifications > 0" class="notification-badge">{{ totalNotifications }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tabs is-boxed mb-4">
      <ul>
        <li :class="{ 'is-active': activeTab === 'dashboard' }">
          <a @click="setActiveTab('dashboard')">
            <span class="icon is-small">
              <i class="fas fa-chart-bar"></i>
            </span>
            <span>Dashboard View</span>
          </a>
        </li>
        <li :class="{ 'is-active': activeTab === 'summary' }">
          <a @click="setActiveTab('summary')">
            <span class="icon is-small">
              <i class="fas fa-table"></i>
            </span>
            <span>Summary Table</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Collapsible Global Filter Bar -->
    <div v-show="showFilters" class="box filter-bar">
      <form @submit.prevent="applyFilters">
        <!-- Row 1: Date Range -->
        <div class="columns is-multiline is-mobile">
          <div class="column is-one-quarter">
            <div class="field">
              <label class="label is-small">Start Date:</label>
              <div class="control">
                <input class="input is-small" type="date" v-model="filters.start_date" />
              </div>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="field">
              <label class="label is-small">End Date:</label>
              <div class="control">
                <input class="input is-small" type="date" v-model="filters.end_date" />
              </div>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="field">
              <label class="label is-small">Department Code:</label>
              <div class="control">
                <multiselect
                  v-model="filters.department_codes"
                  :options="filterOptions.departments"
                  :multiple="true"
                  placeholder="All Departments"
                  :close-on-select="false"
                  track-by="code"
                  label="name">
                </multiselect>
              </div>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="field">
              <label class="label is-small">Status:</label>
              <div class="control">
                <multiselect
                  v-model="filters.status"
                  :options="filterOptions.statuses"
                  :multiple="true"
                  placeholder="All Statuses"
                  :close-on-select="false">
                </multiselect>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 2: Additional Filters -->
        <div class="columns is-multiline is-mobile">
          <div class="column is-one-third">
            <div class="field">
              <label class="label is-small">Invoice Type:</label>
              <div class="control">
                <multiselect
                  v-model="filters.invoice_types"
                  :options="filterOptions.invoiceTypes"
                  :multiple="true"
                  track-by="code"
                  label="description"
                  placeholder="All Invoice Types"
                  :close-on-select="false">
                </multiselect>
              </div>
            </div>
          </div>
          <div class="column is-one-third">
            <div class="field">
              <label class="label is-small">Source System:</label>
              <div class="control">
                <multiselect
                  v-model="filters.source_systems"
                  :options="filterOptions.sourceSystems"
                  :multiple="true"
                  placeholder="All Source Systems"
                  :close-on-select="false"
                  track-by="value"
                  label="label">
                </multiselect>
              </div>
            </div>
          </div>
          <div class="column is-one-third">
            <div class="field is-grouped mt-5">
              <div class="control">
                <button class="button is-primary is-small" type="submit" :disabled="isLoading">
                  <span class="icon is-small">
                    <i class="fas fa-filter"></i>
                  </span>
                  <span>Apply Filters</span>
                </button>
              </div>
              <div class="control">
                <button class="button is-light is-small" type="button" @click="resetFilters">
                  <span class="icon is-small">
                    <i class="fas fa-times"></i>
                  </span>
                  <span>Reset</span>
                </button>
              </div>
              <div class="control">
                <button class="button is-info is-small" type="button" @click="hideFilters">
                  <span class="icon is-small">
                    <i class="fas fa-eye-slash"></i>
                  </span>
                  <span>Hide</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Notification Panel -->
    <div class="notification-panel" :class="{ 'is-active': showNotificationPanel }">
      <div class="notification-panel-overlay" @click="closeNotificationPanel"></div>
      <div class="notification-panel-content">
        <div class="notification-panel-header">
          <h3 class="title is-6">
            <span class="icon">
              <font-awesome-icon icon="bell" />
            </span>
            <span>Notifications</span>
          </h3>
          <button class="delete is-large" @click="closeNotificationPanel"></button>
        </div>
        
        <div class="notification-panel-body">
          <upcoming-alerts 
            :data="alertsData"
            @resubmit="handleResubmit"
          />
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="notification is-info is-light">
      <span class="icon">
        <i class="fas fa-spinner fa-spin"></i>
      </span>
      <span>Applying filters...</span>
    </div>

    <!-- Broadcast Message Modal -->
    <div v-if="showModal" class="modal is-active">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-content">
        <div class="box has-shadow has-background-light p-5">
          <!-- Modal Title -->
          <h2 class="title is-4 has-text-centered has-text-primary">{{ currentMessage.title }}</h2>
          <!-- Modal Content -->
          <div class="content mt-4">
            <p class="is-size-5 has-text-justified">{{ currentMessage.content }}</p>
          </div>
          <!-- Buttons -->
          <div class="buttons is-centered mt-5">
            <button class="button is-link is-light" @click="dismissMessage">
              <span class="icon"><i class="fas fa-times-circle"></i></span>
              <span>Do Not Show This Again</span>
            </button>
            <button
              v-if="hasMoreMessages"
              class="button is-primary is-light"
              @click="nextMessage"
            >
              <span class="icon"><i class="fas fa-arrow-right"></i></span>
              <span>Next</span>
            </button>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="closeModal"></button>
    </div>

    <!-- Main Dashboard Content -->
    <div v-show="activeTab === 'dashboard'" v-if="dataLoaded" class="container">
      <!-- Invoice Summary Panel -->
      <div class="summary-panel">
        <h2 class="title is-5">Invoice Summary</h2>
        <div class="columns is-multiline">
          <div class="column is-one-fifth-desktop is-half-tablet">
            <router-link 
              :to="{ 
                path: '/dashboard/invoices' 
              }">
              <div class="card has-background-primary">
                <div class="card-content has-text-white">
                  <p class="title is-7 has-text-white">Total Invoices</p>
                  <p class="subtitle is-4 has-text-white">{{ data.total_invoices_issued || 0 }}</p>
                </div>
              </div>
            </router-link>
          </div>
          <div class="column is-one-fifth-desktop is-half-tablet">
            <router-link 
              :to="{ 
                path: '/dashboard/invoices', 
                query: { 
                  tab: 'Validated' 
                } 
              }">
              <div class="card has-background-success">
                <div class="card-content has-text-white">
                  <p class="title is-7 has-text-white">Validated</p>
                  <p class="subtitle is-4 has-text-white">{{ data.total_validated_invoices || 0 }}</p>
                </div>
              </div>
            </router-link>
          </div>
          <div class="column is-one-fifth-desktop is-half-tablet">
            <router-link 
              :to="{ 
                path: '/dashboard/invoices', 
                query: { 
                  tab: 'Submission Failed' 
                } 
              }">
              <div class="card has-background-danger">
                <div class="card-content has-text-white">
                  <p class="title is-7 has-text-white">Failed</p>
                  <p class="subtitle is-4 has-text-white">{{ data.submission_failed || 0 }}</p>
                </div>
              </div>
            </router-link>
          </div>
          <div class="column is-one-fifth-desktop is-half-tablet">
            <router-link to="/dashboard/invoices/approval">
              <div class="card has-background-warning">
                <div class="card-content has-text-dark">
                  <p class="title is-7 has-text-dark">Pending Approval</p>
                  <p class="subtitle is-4 has-text-dark">{{ data.pending_approval || 0 }}</p>
                </div>
              </div>
            </router-link>
          </div>
          <div class="column is-one-fifth-desktop is-half-tablet">
            <div class="card has-background-info">
              <div class="card-content has-text-white">
                <p class="title is-7 has-text-white">Success Rate</p>
                <p class="subtitle is-4 has-text-white">{{ data.successful_rate_percentage || 0 }}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Analytics Section -->
      <div class="analytics-section mt-6">
        <h2 class="title is-5">Analytics</h2>
        
        <!-- Invoice Status Breakdown -->
        <div class="columns">
          <div class="column is-6">
            <div v-if="statusBreakdownData && statusBreakdownData.status_distribution && statusBreakdownData.status_distribution.length > 0" :key="`status-chart-wrapper-${dataVersion}`">
              <invoice-status-chart 
                :key="`status-chart-${dataVersion}`"
                :data="getChartData('status')" 
              />
            </div>
            <div v-else class="box has-text-centered">
              <p class="has-text-grey">No status distribution data available</p>
            </div>
          </div>
          <div class="column is-6">
            <div v-if="statusBreakdownData.invoices_by_source_system && statusBreakdownData.invoices_by_source_system.length > 0" :key="`dept-chart-wrapper-${dataVersion}`">
              <invoice-department-chart 
                :key="`dept-chart-${dataVersion}`"
                :data="getChartData('department')" 
              />
            </div>
            <div v-else class="box has-text-centered">
              <p class="has-text-grey">No department data available</p>
            </div>
          </div>
        </div>

        <!-- Monthly Performance Trends -->
        <div class="columns mt-4">
          <div class="column is-12">
            <div v-if="performanceTrendsData && performanceTrendsData.submission_trend && performanceTrendsData.submission_trend.length > 0" :key="`trends-chart-wrapper-${dataVersion}`">
              <performance-trends-chart 
                :key="`trends-chart-${dataVersion}`"
                :data="getChartData('trends')" 
              />
            </div>
            <div v-else class="box has-text-centered">
              <p class="has-text-grey">No performance trends data available</p>
              <p class="has-text-grey is-size-7">Debug: {{ JSON.stringify(performanceTrendsData) }}</p>
            </div>
          </div>
        </div>

        <!-- Time period charts -->
        <div class="columns mt-4">
          <div class="column is-12">
            <div v-if="statusBreakdownData.invoices_by_time && statusBreakdownData.invoices_by_time.length > 0" :key="`time-chart-wrapper-${dataVersion}`">
              <invoice-time-chart 
                :key="`time-chart-${dataVersion}`"
                :data="getChartData('time')" 
                @period-change="handlePeriodChange"
              />
            </div>
            <div v-else class="box has-text-centered">
              <p class="has-text-grey">No time-based data available</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Logs Section -->
      <div class="alerts-section mt-6">
        <h2 class="title is-5">Error Logs</h2>
        
        <!-- Error Logs -->
        <div class="columns">
          <div class="column is-12">
            <!-- Loading indicator for error logs -->
            <div v-if="errorLogsLoading" class="box has-text-centered">
              <span class="icon">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span class="ml-2">Loading error logs...</span>
            </div>
            
            <!-- Error logs table -->
            <error-logs-table 
              v-else-if="!errorLogsLoading && errorLogsData && errorLogsData.results && errorLogsData.results.length > 0"
              :key="`error-logs-${dataVersion}`"
              :data="errorLogsData"
              :loading="errorLogsLoading"
              @resubmit="handleResubmit"
              @page-change="handleErrorLogsPageChange"
              @refresh="handleErrorLogsRefresh"
            />
            
            <!-- No data state -->
            <div v-else class="box has-text-centered">
              <p class="has-text-grey">No error logs available</p>
              <p class="has-text-grey is-size-7">Debug: {{ JSON.stringify(errorLogsData) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Summary Table View -->
    <div v-show="activeTab === 'summary'">
      <summary-table-view />
    </div>
    
    <!-- Loading state for initial load -->
    <div v-if="!dataLoaded" class="container">
      <div class="notification is-info is-light">
        <span class="icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        <span>Loading dashboard data...</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toRaw } from 'vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

// Dashboard components
import InvoiceStatusChart from '@/components/dashboard/InvoiceStatusChart.vue';
import InvoiceDepartmentChart from '@/components/dashboard/InvoiceDepartmentChart.vue';
import PerformanceTrendsChart from '@/components/dashboard/PerformanceTrendsChart.vue';
import InvoiceTimeChart from '@/components/dashboard/InvoiceTimeChart.vue';
import ErrorLogsTable from '@/components/dashboard/ErrorLogsTable.vue';
import UpcomingAlerts from '@/components/dashboard/UpcomingAlerts.vue';
import SummaryTableView from '@/components/dashboard/SummaryTableView.vue';

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default {
  name: 'UserDashboard',
  components: {
    Multiselect,
    InvoiceStatusChart,
    InvoiceDepartmentChart,
    PerformanceTrendsChart,
    InvoiceTimeChart,
    ErrorLogsTable,
    UpcomingAlerts,
    SummaryTableView,
  },
  data() {
    return {
      isLoading: false,
      dataLoaded: false, // Track if initial data is loaded
      activeTab: 'dashboard', // Current active tab (dashboard or summary)
      showFilters: false, // Filters start hidden
      quickFilter: '', // Quick filter dropdown
      
      // Global Filters
      filters: {
        start_date: '',
        end_date: '',
        department_codes: [],
        status: [],
        invoice_types: [],
        source_systems: []
      },
      
      // Filter Options
      filterOptions: {
        departments: [],
        statuses: [],
        invoiceTypes: [],
        sourceSystems: [
          { label: 'QUANTUM', value: 'QUANTUM' },
          { label: 'AZENTIO', value: 'AZENTIO' },
          { label: 'ELMS', value: 'ELMS' },
          { label: 'F1', value: 'F1' },
          { label: 'DESPAQ', value: 'DESPAQ' }
        ]
      },
      
      // Broadcast Messages
      messages: [],
      currentIndex: 0,
      showModal: false,
      
      // Notification Panel
      showNotificationPanel: false,
      
      // Dashboard data with proper defaults
      data: {
        total_invoices_issued: 0,
        total_validated_invoices: 0,
        submission_failed: 0,
        pending_submission: 0,
        pending_approval: 0,
        successful_rate_percentage: 0
      },
      statusBreakdownData: {
        status_distribution: [],
        invoices_by_source_system: [],
        invoices_by_time: []
      },
      performanceTrendsData: {
        submission_trend: []
      },
      errorLogsData: {
        results: [],
        count: 0
      },
      errorLogsLoading: false, // Track error logs loading state separately
      alertsData: {
        expiring_soon: [],
        resubmit_reminders: [],
        cancelled_pending_approval: []
      },
      dataVersion: 0, // Used for forcing component re-render
      debounceTimer: null, // For debouncing quick filter changes
      
      // Static chart data to completely isolate from reactivity
      chartDataCache: {
        status: { status_distribution: [] },
        department: [],
        trends: { submission_trend: [] },
        time: []
      }
    };
  },
  computed: {
    canApprove() {
      return this.$store.getters.canApproveInvoice;
    },
    canCreateInvoice() {
      return this.$store.getters.canCreateInvoice;
    },
    currentMessage() {
      return this.messages[this.currentIndex] || {};
    },
    hasMoreMessages() {
      return this.currentIndex < this.messages.length - 1;
    },
    hasNotifications() {
      return this.totalNotifications > 0;
    },
    totalNotifications() {
      if (!this.alertsData) return 0;
      
      const expiringCount = this.alertsData.expiring_soon ? this.alertsData.expiring_soon.length : 0;
      const resubmitCount = this.alertsData.resubmit_reminders ? this.alertsData.resubmit_reminders.length : 0;
      const pendingCount = this.alertsData.cancelled_pending_approval ? this.alertsData.cancelled_pending_approval.length : 0;
      
      return expiringCount + resubmitCount + pendingCount;
    }
  },
  
  methods: {
    formatNumber(value) {
      return value.toLocaleString();
    },
    
    // Toggle filter visibility
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },
    
    // Hide filters
    hideFilters() {
      this.showFilters = false;
    },
    
    // Notification Panel Methods
    toggleNotificationPanel() {
      this.showNotificationPanel = !this.showNotificationPanel;
    },
    
    closeNotificationPanel() {
      this.showNotificationPanel = false;
    },
    
    // Quick filter methods
    async applyQuickFilter() {
      if (this.isLoading) return; // Prevent multiple simultaneous calls
      
      // Clear any existing debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Debounce the API call
      this.debounceTimer = setTimeout(async () => {
        const today = new Date();
        let startDate = '';
        let endDate = '';
        
        switch (this.quickFilter) {
          case 'today': {
            startDate = formatLocalDate(today);
            endDate = formatLocalDate(today);
            break;
          }
          case 'yesterday': {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            startDate = formatLocalDate(yesterday);
            endDate = formatLocalDate(yesterday);
            break;
          }
          case 'this_week': {
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startDate = formatLocalDate(startOfWeek);
            endDate = formatLocalDate(today);
            break;
          }
          case 'this_month': {
            startDate = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
            endDate = formatLocalDate(today);
            break;
          }
          case 'last_month': {
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
            startDate = formatLocalDate(lastMonth);
            endDate = formatLocalDate(lastMonthEnd);
            break;
          }
          default: {
            // All time - clear dates
            startDate = '';
            endDate = '';
          }
        }
        
        this.filters.start_date = startDate;
        this.filters.end_date = endDate;
        
        await this.applyFilters();
      }, 300); // 300ms debounce
    },
    
    // Global Filter Methods
    async applyFilters() {
      if (this.isLoading) return; // Prevent multiple simultaneous calls
      
      this.isLoading = true;
      try {
        console.log('UserDashboard: Applying filters...');
        
        // Build filter parameters
        const params = this.buildFilterParams();
        
        // Fetch all dashboard data with filters (this calls updateChartCache internally)
        await this.fetchFilteredDashboardData(params);
        
        // Wait for DOM to update before incrementing version
        await this.$nextTick();
        
        // Additional delay to ensure all components are ready for re-render
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Increment data version to force complete component re-render
        this.dataVersion++;
        
        console.log('UserDashboard: Dashboard re-render triggered, dataVersion:', this.dataVersion);
        
      } catch (error) {
        console.error('UserDashboard: Error applying filters:', error);
        this.$toast && this.$toast.error('Error applying filters. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    
    resetFilters() {
      this.filters = {
        start_date: '',
        end_date: '',
        department_codes: [],
        status: [],
        invoice_types: [],
        source_systems: []
      };
      this.quickFilter = '';
      
      // Fetch unfiltered data
      this.fetchData();
    },
    
    buildFilterParams() {
      const params = new URLSearchParams();
      
      if (this.filters.start_date) {
        params.append('start_date', this.filters.start_date);
      }
      if (this.filters.end_date) {
        params.append('end_date', this.filters.end_date);
      }
      if (this.filters.department_codes.length > 0) {
        this.filters.department_codes.forEach(dept => {
          params.append('department_codes', dept.code);
        });
      }
      if (this.filters.status.length > 0) {
        this.filters.status.forEach(status => {
          params.append('status', status);
        });
      }
      if (this.filters.invoice_types.length > 0) {
        this.filters.invoice_types.forEach(type => {
          params.append('invoice_types', type.code);
        });
      }
      if (this.filters.source_systems.length > 0) {
        this.filters.source_systems.forEach(system => {
          params.append('source_systems', system.value);
        });
      }
      
      return params;
    },
    
    async fetchFilteredDashboardData(params) {
      try {
        // Helper function to deep clone data and remove Vue reactivity
        const cloneData = (data) => {
          return JSON.parse(JSON.stringify(toRaw(data)));
        };

        // 1. Invoice Summary Panel
        const summaryResponse = await axios.get(`/api/v1/invoice-summary-panel/?${params}`);
        this.data = cloneData(summaryResponse.data) || {
          total_invoices_issued: 0,
          total_validated_invoices: 0,
          submission_failed: 0,
          pending_submission: 0,
          pending_approval: 0,
          successful_rate_percentage: 0
        };

        // 2. Invoice Status Breakdown
        const statusResponse = await axios.get(`/api/v1/invoice-status-breakdown/?${params}`);
        this.statusBreakdownData = cloneData(statusResponse.data) || {
          status_distribution: [],
          invoices_by_source_system: [],
          invoices_by_time: []
        };

        // 3. Monthly Performance Trends
        const trendsResponse = await axios.get(`/api/v1/monthly-performance-trends/?${params}`);
        this.performanceTrendsData = cloneData(trendsResponse.data) || {
          submission_trend: []
        };

        // 4. Error Logs - Load independently in background
        this.fetchErrorLogsIndependently(params);

        // 5. Upcoming Actions / Alerts - Updated for filtered data
        try {
          const alertsResponse = await axios.get(`/api/v1/upcoming-actions-alerts/?${params}`);
          console.log('Filtered alerts response:', alertsResponse.data);
          this.alertsData = cloneData(alertsResponse.data) || {
            expiring_soon: [],
            resubmit_reminders: [],
            cancelled_pending_approval: []
          };
        } catch (error) {
          console.error('Error fetching filtered alerts:', error);
          this.alertsData = {
            expiring_soon: [],
            resubmit_reminders: [],
            cancelled_pending_approval: []
          };
        }
        
        // Update chart cache after all data is fetched
        this.updateChartCache();
        
      } catch (error) {
        console.error('Error fetching filtered dashboard data:', error);
      }
    },
    
    // Fetch Filter Options
    async fetchFilterOptions() {
      try {
        const response = await axios.get('/api/v1/unique-types/');
        
        // Transform invoice types from simple strings to objects for multiselect
        this.filterOptions.invoiceTypes = (response.data.invoice_types || []).map(type => ({
          code: type,
          description: `Invoice Type ${type}`
        }));
        
        this.filterOptions.statuses = response.data.status_choices || [];
        
        // Fetch departments from store codes
        const deptResponse = await axios.get('/api/v1/all-store-codes/');
        this.filterOptions.departments = deptResponse.data || [];
        
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    },
    
    // Original Methods
    async fetchData() {
      try {
        console.log('UserDashboard: Starting data fetch...');
        
        await this.fetchDashboardData();
        this.fetchMessages();
        
        // Update chart cache after all data is fetched
        this.updateChartCache();
        
        // Set data loaded flag
        this.dataLoaded = true;
        
        // Wait for data to be processed and DOM to update
        await this.$nextTick();
        
        // Additional delay to ensure all chart components are ready
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Increment version to trigger chart re-render
        this.dataVersion++;
        
        console.log('UserDashboard: Initial dashboard load complete, dataVersion:', this.dataVersion);
      } catch (error) {
        console.error('UserDashboard: Error in fetchData:', error);
      }
    },
    
    async fetchDashboardData() {
      try {
        console.log('Axios base URL:', axios.defaults.baseURL);
        console.log('Authorization header:', axios.defaults.headers.common['Authorization']);
        
        // Helper function to deep clone data and remove Vue reactivity
        const cloneData = (data) => {
          return JSON.parse(JSON.stringify(toRaw(data)));
        };
        
        // 1. Invoice Summary Panel
        const summaryResponse = await axios.get('/api/v1/invoice-summary-panel/');
        this.data = cloneData(summaryResponse.data) || {
          total_invoices_issued: 0,
          total_validated_invoices: 0,
          submission_failed: 0,
          pending_submission: 0,
          pending_approval: 0,
          successful_rate_percentage: 0
        };

        // 2. Invoice Status Breakdown
        const statusResponse = await axios.get('/api/v1/invoice-status-breakdown/');
        this.statusBreakdownData = cloneData(statusResponse.data) || {
          status_distribution: [],
          invoices_by_source_system: [],
          invoices_by_time: []
        };

        // 3. Monthly Performance Trends
        const trendsResponse = await axios.get('/api/v1/monthly-performance-trends/');
        this.performanceTrendsData = cloneData(trendsResponse.data) || {
          submission_trend: []
        };

        // 4. Error Logs - Load independently in background
        this.fetchErrorLogsIndependently();

        // 5. Upcoming Actions / Alerts - Updated endpoint and data handling
        try {
          const alertsResponse = await axios.get('/api/v1/upcoming-actions-alerts/');
          console.log('Upcoming alerts response:', alertsResponse.data);
          this.alertsData = cloneData(alertsResponse.data) || {
            expiring_soon: [],
            resubmit_reminders: [],
            cancelled_pending_approval: []
          };
          console.log('Alerts data assigned:', this.alertsData);
        } catch (error) {
          console.error('Error fetching upcoming alerts:', error);
          this.alertsData = {
            expiring_soon: [],
            resubmit_reminders: [],
            cancelled_pending_approval: []
          };
        }
        
        // Update chart cache after all data is fetched
        this.updateChartCache();
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    },

    // New method to fetch error logs independently
    async fetchErrorLogsIndependently(params = null) {
      try {
        this.errorLogsLoading = true;
        
        // Helper function to deep clone data and remove Vue reactivity
        const cloneData = (data) => {
          return JSON.parse(JSON.stringify(toRaw(data)));
        };
        
        // Use provided params or build from current filters
        const queryParams = params || this.buildFilterParams();
        
        console.log('Fetching error logs independently...');
        const errorResponse = await axios.get(`/api/v1/error-logs/?${queryParams}`);
        console.log('Error logs response:', errorResponse.data);
        
        this.errorLogsData = cloneData(errorResponse.data) || {
          results: [],
          count: 0
        };
        console.log('Error logs data assigned:', this.errorLogsData);
        
      } catch (error) {
        console.error('Error fetching error logs independently:', error);
        this.errorLogsData = {
          results: [],
          count: 0
        };
      } finally {
        this.errorLogsLoading = false;
      }
    },

    // Component handler methods
    handlePeriodChange(period) {
      this.fetchStatusBreakdownData(period);
    },

    async fetchStatusBreakdownData(period = 'day') {
      try {
        // Helper function to deep clone data and remove Vue reactivity
        const cloneData = (data) => {
          return JSON.parse(JSON.stringify(toRaw(data)));
        };
        
        const params = this.buildFilterParams();
        params.append('period', period);
        const response = await axios.get(`/api/v1/invoice-status-breakdown/?${params}`);
        this.statusBreakdownData = cloneData(response.data) || {
          status_distribution: [],
          invoices_by_source_system: [],
          invoices_by_time: []
        };
        
        // Update chart cache
        this.updateChartCache();
      } catch (error) {
        console.error('Error fetching status breakdown data:', error);
      }
    },

    handleErrorLogsPageChange(page) {
      this.fetchErrorLogs(page);
    },

    handleErrorLogsRefresh() {
      this.fetchErrorLogsIndependently();
    },

    async fetchErrorLogs(page = 1) {
      try {
        this.errorLogsLoading = true;
        
        // Helper function to deep clone data and remove Vue reactivity
        const cloneData = (data) => {
          return JSON.parse(JSON.stringify(toRaw(data)));
        };
        
        const params = this.buildFilterParams();
        params.append('page', page);
        const response = await axios.get(`/api/v1/error-logs/?${params}`);
        this.errorLogsData = cloneData(response.data) || {
          results: [],
          count: 0
        };
      } catch (error) {
        console.error('Error fetching error logs:', error);
        this.errorLogsData = {
          results: [],
          count: 0
        };
      } finally {
        this.errorLogsLoading = false;
      }
    },

    handleResubmit(invoiceId) {
      console.log('Resubmit invoice:', invoiceId);
      // Add your resubmission API call here
    },
    
    fetchMessages() {
      axios.get('/api/v1/notifications/messages/').then((response) => {
        this.messages = response.data;
        if (this.messages.length > 0) {
          this.showModal = true;
        }
      });
    },
    dismissMessage() {
      const messageId = this.currentMessage.id;
      axios
        .post('/api/v1/notifications/messages/dismiss/', { message_id: messageId })
        .then(() => {
          this.messages.splice(this.currentIndex, 1);
          if (this.messages.length === 0) {
            this.showModal = false;
          } else if (this.currentIndex >= this.messages.length) {
            this.currentIndex = 0;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    nextMessage() {
      this.currentIndex += 1;
    },
    closeModal() {
      this.showModal = false;
    },
    
    // Handle keyboard events
    handleKeydown(event) {
      if (event.key === 'Escape' && this.showNotificationPanel) {
        this.closeNotificationPanel();
      }
    },
    
    // Get clean chart data to prevent reactivity issues
    getChartData(type) {
      try {
        // Return cached static data that is completely isolated from Vue reactivity
        return this.chartDataCache[type] || (type === 'status' || type === 'trends' ? {} : []);
      } catch (error) {
        console.error(`Error getting chart data for type ${type}:`, error);
        return type === 'status' || type === 'trends' ? {} : [];
      }
    },
    
    // Update chart cache with completely non-reactive data
    updateChartCache() {
      try {
        // Create completely static data copies
        this.chartDataCache.status = {
          status_distribution: this.statusBreakdownData?.status_distribution ? 
            JSON.parse(JSON.stringify(this.statusBreakdownData.status_distribution)) : []
        };
        
        this.chartDataCache.department = this.statusBreakdownData?.invoices_by_source_system ? 
          JSON.parse(JSON.stringify(this.statusBreakdownData.invoices_by_source_system)) : [];
          
        this.chartDataCache.trends = {
          submission_trend: this.performanceTrendsData?.submission_trend ? 
            JSON.parse(JSON.stringify(this.performanceTrendsData.submission_trend)) : []
        };
        
        this.chartDataCache.time = this.statusBreakdownData?.invoices_by_time ? 
          JSON.parse(JSON.stringify(this.statusBreakdownData.invoices_by_time)) : [];
          
      } catch (error) {
        console.error('Error updating chart cache:', error);
      }
    },
  },
  mounted() {
    this.fetchData();
    this.fetchMessages();
    this.fetchFilterOptions();
    
    // Add keyboard event listener
    document.addEventListener('keydown', this.handleKeydown);
  },
  
  beforeUnmount() {
    // Clear any pending debounce timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeydown);
  },
  
};
</script>

<style scoped>
@import 'vue-multiselect/dist/vue-multiselect.css';

.user-dashboard {
  padding: 20px;
}

.level {
  margin-bottom: 2rem;
}

.filter-bar {
  margin-bottom: 2rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 1rem;
}

.card-content {
  cursor: pointer;
}

.summary-panel {
  margin-bottom: 3rem;
}

.analytics-section,
.alerts-section {
  margin-bottom: 3rem;
}

.card.has-background-primary {
  background-color: #0a1821 !important;
}

.card.has-background-success {
  background-color: #0a3954 !important;
}

.card.has-background-danger {
  background-color: #dc3545 !important;
}

.card.has-background-warning {
  background-color: #ffc107 !important;
}

.card.has-background-grey {
  background-color: #ffc107 !important;
}

.card.has-background-info {
  background-color: #17a2b8 !important;
}

.multiselect {
  min-height: 32px;
}

.multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 4px;
}

.multiselect__input {
  font-size: 0.75rem;
}

.multiselect__single {
  font-size: 0.75rem;
  padding-left: 4px;
}

.select.is-small {
  font-size: 0.75rem;
}

/* Notification Button */
.notification-button {
  position: relative;
}

.notification-button.has-notifications {
  animation: pulse 2s infinite;
}

.notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff3860;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

/* Notification Panel */
.notification-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  z-index: 1000;
  pointer-events: none;
  transition: all 0.3s ease;
}

.notification-panel.is-active {
  pointer-events: all;
}

.notification-panel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.notification-panel.is-active .notification-panel-overlay {
  opacity: 1;
}

.notification-panel-content {
  position: absolute;
  top: 0;
  right: 0;
  width: 450px;
  height: 100vh;
  background-color: #f8f9fa;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
  transform: translateX(100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  border-left: 2px solid #0a3954;
}

.notification-panel.is-active .notification-panel-content {
  transform: translateX(0);
}

.notification-panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #e9ecef;
  color: #495057;
}

.notification-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background-color: #f8f9fa;
  color: #495057;
}

/* Pulse animation for notification button */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(10, 57, 84, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(10, 57, 84, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 57, 84, 0);
  }
}

/* Responsive design for notification panel */
@media screen and (max-width: 768px) {
  .notification-panel-content {
    width: 100%;
  }
}

/* Tab styling to match theme */
.tabs.is-boxed li.is-active a {
  background-color: #0a1821;
  border-color: #0a1821;
  color: white;
}

.tabs.is-boxed a {
  border: 1px solid #dbdbdb;
  border-radius: 4px 4px 0 0;
  color: #363636;
}

.tabs.is-boxed a:hover {
  background-color: #f5f5f5;
  border-color: #b5b5b5;
}
</style>



